import { ref } from 'vue'
import { Platform } from 'quasar'
import { Parse } from 'src/boot/parse'
// Static import (like statusbar.ts / useAppUpdate.ts) so the Capacitor runtime
// actually registers the plugin. A DYNAMIC import() was code-split into a chunk that
// failed to resolve inside the Android webview → getPlugin() returned null → the
// "Enable push" toggle never turned on. Importing the module on web is harmless; we
// never call its native methods there (the isSupported guard short-circuits first).
import { PushNotifications } from '@capacitor/push-notifications'

/**
 * usePushNotifications — native (FCM) push registration for the Planner admin app.
 *
 * WHY: a Google Play reviewer asked for new-booking notifications. The server side
 * (Parse `handlePlannerOrderCreated`) already gathers admin + sub-admin device tokens
 * and decides who to notify; the only missing client piece was actually OBTAINING a
 * device token and writing it to the logged-in User's `fcm_token`. This composable
 * does exactly that, plus the three per-admin "which alerts do I want" flags.
 *
 * Native-only: push is a no-op on the web build (Platform.is.capacitor === false) —
 * every native call is gated behind isSupported.
 */

// Per-USER (per-admin) preference fields, stored on the Parse User so each admin/
// sub-admin chooses independently — mirrors the server's pref gate names.
export type PushPrefKey = 'pl_push_client_new' | 'pl_push_new' | 'pl_push_update'

type PermissionState = 'unknown' | 'prompt' | 'granted' | 'denied'

// Module-level singletons (thin-composable pattern, like useAuth) so every caller
// shares one registration state.
const isRegistering = ref(false)
const permission = ref<PermissionState>('unknown')
const deviceToken = ref<string | null>(null)
// Surfaced on-screen in the dialog so we can diagnose device-only failures without
// a remote debugger (e.g. "plugin not registered" vs a permission error).
const lastError = ref<string>('')
// Reactive "push enabled on THIS device" — drives the dashboard status badge
// (Enabled / Disabled), mirroring the dialog's top toggle. Kept in sync on
// enable/disable, via syncEnabled(), and by refreshTokenIfEnabled() on boot/open.
const enabled = ref<boolean>(false)
let listenersBound = false

// Push is only meaningful inside the native (Capacitor) shell.
const isSupported = Platform.is.capacitor === true

// Plugin handle — null on web (or if the native plugin isn't registered).
function getPlugin(): typeof PushNotifications | null {
  if (!isSupported) return null
  return PushNotifications || null
}

// Persist push settings via a Cloud Function (master key). We can't do a plain
// user.save() because the server runs allowClientClassCreation:false — adding the
// new pl_push_* columns from the client would be rejected. After saving we fetch()
// so the local current-user cache reflects the change for getPref()/hasStoredToken().
async function persist(settings: { fcm_token?: string | null; add_fcm_token?: string; remove_fcm_token?: string; prefs?: Partial<Record<PushPrefKey, boolean>> }) {
  await Parse.Cloud.run('savePlannerPushSettings', settings)
  try { await Parse.User.current()?.fetch() } catch { /* keep going on a transient fetch error */ }
}

// Recompute the dashboard Enabled/Disabled badge. It mirrors the dialog's top
// "Enable push on THIS device" toggle — i.e. whether THIS device currently has OS
// push permission. It deliberately does NOT reflect the per-alert sub-toggles, nor
// whether OTHER devices the admin owns are enabled. No-op (Disabled) on web / when
// the native plugin isn't present.
async function syncEnabled() {
  const plugin = getPlugin()
  if (!plugin) { enabled.value = false; return }
  try {
    enabled.value = (await plugin.checkPermissions()).receive === 'granted'
  } catch {
    enabled.value = false
  }
}

// Persist the FCM registration token onto the current User. The server reads
// User.fcm_token when fanning out booking alerts.
async function saveToken(token: string) {
  deviceToken.value = token
  const user = Parse.User.current()
  if (!user) return
  // Multi-device: add THIS device's token to the user's token SET (the server fans out
  // to every token, so an admin gets push on all their devices). Skip if already present.
  const existing: string[] = user.get('fcm_tokens') || []
  if (existing.includes(token)) { enabled.value = true; return }
  try {
    await persist({ add_fcm_token: token })
    enabled.value = true
    console.log('[push] saved fcm_token')
  } catch (e) {
    console.warn('[push] failed to save fcm_token', e)
  }
}

async function bindListeners(PushNotifications: any) {
  if (listenersBound) return
  listenersBound = true

  PushNotifications.addListener('registration', (t: { value: string }) => {
    console.log('[push] registration token received')
    saveToken(t.value)
  })
  PushNotifications.addListener('registrationError', (err: any) => {
    console.warn('[push] registration error', err)
  })
  // Foreground receipt + tap are left to the OS default (system tray notification).
  // A future iteration can deep-link a tap to the booking via notification.data.
  PushNotifications.addListener('pushNotificationActionPerformed', (action: any) => {
    console.log('[push] notification tapped', action?.notification?.data)
  })
}

// ─── Eager init (native) ──────────────────────────────────────────────────────
// Bind the native listeners at MODULE LOAD — before onMounted / enablePush / any
// register() call. FCM can emit the 'registration' token at app startup on its own
// (first registration or token refresh); if no listener is attached yet the token is
// dropped — logcat shows "No listeners found for event registration" — so it's never
// saved to User.fcm_token, the server has no device to target, and no push arrives.
// Binding here guarantees we always capture the token. (Harmless no-op on web.)
if (isSupported) {
  void bindListeners(getPlugin())
}

// Best-effort initial badge state (this device's push permission). The dashboard
// also calls syncEnabled() on mount, and refreshTokenIfEnabled() keeps it current.
void syncEnabled()

export default function usePushNotifications() {
  /**
   * Request OS permission (if needed) and register for a device token.
   * Returns the resulting permission state. Safe to call repeatedly.
   */
  async function enablePush(): Promise<PermissionState> {
    lastError.value = ''
    const plugin = getPlugin()
    if (!plugin) {
      permission.value = 'denied'
      lastError.value = 'Push plugin not available on this build (run cap sync + rebuild).'
      return permission.value
    }
    isRegistering.value = true
    try {
      let perm = await plugin.checkPermissions()
      if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
        perm = await plugin.requestPermissions()
      }
      if (perm.receive !== 'granted') {
        permission.value = 'denied'
        return permission.value
      }
      permission.value = 'granted'
      await bindListeners(plugin)
      // Triggers the 'registration' listener → saveToken().
      await plugin.register()
      return permission.value
    } catch (e: any) {
      console.warn('[push] enablePush failed', e)
      permission.value = 'denied'
      lastError.value = String(e?.message || e)
      return permission.value
    } finally {
      isRegistering.value = false
    }
  }

  /**
   * Stop receiving pushes on THIS device by clearing the token from the User.
   * (We can't revoke the OS-level grant from here; clearing the token is what
   * removes this device from the server's fan-out.)
   */
  async function disablePush(): Promise<void> {
    const token = deviceToken.value
    deviceToken.value = null
    const user = Parse.User.current()
    if (!user) return
    try {
      // Remove only THIS device's token — other devices for this admin stay subscribed.
      // (Fallback to clearing the legacy single field if this device's token is unknown.)
      await persist(token ? { remove_fcm_token: token } : { fcm_token: null })
      enabled.value = false
    } catch (e) {
      console.warn('[push] disablePush failed', e)
    }
  }

  /**
   * Called on app start: if this admin still has a saved token, silently re-register
   * so a rotated FCM token is refreshed. No-op on web or when permission isn't granted.
   */
  // Returns true if THIS device has OS push permission (and was (re)registered).
  // Used both on boot and as the dialog's "enabled on this device" signal.
  async function refreshTokenIfEnabled(): Promise<boolean> {
    if (!isSupported) { enabled.value = false; return false }
    const plugin = getPlugin()
    if (!plugin) { enabled.value = false; return false }
    await bindListeners(plugin)
    try {
      const perm = await plugin.checkPermissions()
      permission.value = perm.receive === 'granted' ? 'granted'
        : perm.receive === 'denied' ? 'denied' : 'prompt'
      const granted = perm.receive === 'granted'
      enabled.value = granted // keep the dashboard badge in lockstep with the top toggle
      if (!granted) return false
      // Re-register whenever OS permission is granted — do NOT gate on a stored token
      // (one dropped by an earlier race/overwrite would otherwise never recover).
      // register() re-fetches the token → 'registration' → saveToken re-adds it.
      await plugin.register()
      return true
    } catch (e) {
      console.warn('[push] refresh failed', e)
      enabled.value = false
      return false
    }
  }

  // True only when THIS device's token is in the user's set. Must NOT fall back to
  // "any token exists" — otherwise a device that never registered (e.g. a fresh iPhone)
  // would show as enabled just because another device (the Android tablet) registered,
  // which is the exact bug that left the iOS toggle stuck on "Enabled".
  // (The dashboard badge mirrors this same THIS-device notion via `enabled`/syncEnabled.)
  function hasStoredToken(): boolean {
    const t = deviceToken.value
    if (!t) return false
    const user = Parse.User.current()
    if (!user) return false
    const tokens: string[] = user.get('fcm_tokens') || []
    return tokens.includes(t) || user.get('fcm_token') === t
  }

  // Read/write the per-admin alert toggles on the current User.
  function getPref(key: PushPrefKey): boolean {
    return Parse.User.current()?.get(key) === true
  }

  async function setPref(key: PushPrefKey, value: boolean): Promise<void> {
    if (!Parse.User.current()) return
    await persist({ prefs: { [key]: value } })
  }

  return {
    isSupported,
    isRegistering,
    permission,
    deviceToken,
    lastError,
    enabled,
    syncEnabled,
    enablePush,
    disablePush,
    refreshTokenIfEnabled,
    hasStoredToken,
    getPref,
    setPref,
  }
}
